import React, { useContext } from 'react';
import { TABS_MENU } from 'redhotpie-shared-lib/constants/constants';
import { IApiResponse } from 'redhotpie-shared-lib/interfaces/IApiResponse';

// AppContext lives in its own module so that consumers (registration steps,
// modals, hooks) can import `useApp` without pulling the whole App shell
// (components/App.tsx) and everything it imports into their chunk.

export interface AppContextProps {
  timeLeft: number;
  timeLeftString: string;
  isBoosted: boolean;
  setShowBoostSuccess: (toggle: boolean) => void;
  handleShowBoostProfileModal: (params: any) => void;
  handleShowBoostConversationModal: (params: any) => void;
  handleShowGetMoreProduct: (type: string) => void;
  handleCheckBoostProfile: (callback?: (canBoost: boolean) => void) => boolean;
  isOutAU: boolean;
  currentStoreCredits: number | null;
  userInfo: any;
  history: any;
  handleOpenRHPCoins: (amount?: number, callback?: () => void) => void;
  onCloseProfileMatchModal: (dataKey: string[], data: any) => void;
  handleOpenCommentModal: (options: {
    funcGetComment: any;
    funcGetReply: any;
    contentItem: any;
    funcClickLike: any;
    funcCreateComment: any;
    funcCallbackDeleteComment: any;
    isOwner?: boolean;
    verifyBeforeDeleteComment?: any;
  }) => void;
  handleOpenLikeModal: (options: {
    contentItem: any;
    funcGetLike: (params: any) => any;
    funcLikeUnlikeItem?: (e?: React.MouseEvent, toggle?: boolean) => Promise<void>;
    callbackCloseModal?: () => void;
  }) => void;
  handleOpenProfileCommon: (user: any) => void;
  handleOpenCommonPhotoModal: (options: {
    photos: any[];
    totalItems: number;
    getComment: (params: any) => void;
    getLike: (params: any) => void;
    idxImageSelected: number;
    setIndexImageSelected: (index: number) => void;
    handleUpdateCountComment: (step: number) => void;
    callbackDeleteComment: (res: IApiResponse) => void;
    handleClickLikePhoto: (contentItem: any, callback: () => void) => void;
    imgContentItem?: any;
  }) => void;
  handleAppGoBack: () => void;
  handleOpenEventDetail: (event: any, eventId: number, tabDefault?: number) => void;
  handleGetVerified: () => void;
  vericalScrollTo: (y: number) => void;
  translateTextStatic: (text: string, params?: any) => string;
}

export const AppContext = React.createContext<AppContextProps>({
  timeLeft: 0,
  timeLeftString: '',
  isBoosted: false,
  setShowBoostSuccess: toggle => {},
  handleShowBoostProfileModal: isAllowCheckFirst => {},
  handleShowBoostConversationModal: isAllowCheckFirst => {},
  handleShowGetMoreProduct: type => {},
  handleCheckBoostProfile: callback => false,
  isOutAU: false,
  currentStoreCredits: null,
  userInfo: undefined,
  history: null,
  handleOpenRHPCoins: (amount = 0) => {},
  onCloseProfileMatchModal: (dataKey, data) => {},
  handleOpenCommentModal: options => {},
  handleOpenLikeModal: ({ contentItem, funcGetLike, funcLikeUnlikeItem, callbackCloseModal = () => {} }) => {},
  handleOpenProfileCommon: user => {},
  handleOpenCommonPhotoModal: ({
    photos,
    totalItems,
    getComment,
    getLike,
    idxImageSelected,
    setIndexImageSelected,
    handleUpdateCountComment,
    callbackDeleteComment,
    handleClickLikePhoto,
    imgContentItem,
  }) => {},
  handleAppGoBack: () => {},
  handleOpenEventDetail: (event, eventId, tabDefault = TABS_MENU.EVENT_DETAIL[0].id) => {},
  handleGetVerified: () => {},
  vericalScrollTo: y => {},
  translateTextStatic: (text: string, params: any) => '',
});

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within AppContext.Provider');
  }
  return context;
};
